import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { UserSelectors } from '@states/user/user.selector-types';
import { UserModel } from '@models/user.model';
import { filter, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '../authentication/authentication.service';
import { AuthSessionService } from '../authentication/auth-session.service';
import { AppState } from '../store/app.state';
import { UserState } from '@states/user/user.reducer';
import { UniversalLoginService } from '../authentication/universal-login.service';

interface RoleGuardOptions {
  allowedRole: UserModel.Role;
  redirectByRole: Partial<Record<UserModel.Role, UserModel.Role>>;
  fallbackRedirectRole?: UserModel.Role;
}

export function createRoleGuard(options: RoleGuardOptions) {
  const router = inject(Router);
  const authenticationService = inject(AuthenticationService);
  const authSessionService = inject(AuthSessionService);
  const universalLoginService = inject(UniversalLoginService);
  const store = inject<Store<AppState>>(Store);

  return from(ensureLoggedIn(authenticationService, authSessionService)).pipe(
    switchMap((isLoggedIn) => {
      if (!isLoggedIn) {
        universalLoginService.redirectToLogin();
        return [false];
      }

      return store.select(UserSelectors.selectUser).pipe(
        filter((user: UserState) => !!user.isProfileLoaded),
        map((user: UserState) => resolveRoleAccess(user, router, options)),
      );
    }),
  );
}

async function ensureLoggedIn(
  authenticationService: AuthenticationService,
  authSessionService: AuthSessionService,
): Promise<boolean> {
  if (authenticationService.isLoggedIn()) {
    return true;
  }

  return authSessionService.ensureValidSession({
    allowBackgroundRefresh: false,
    isSilentRefresh: true,
  });
}

function resolveRoleAccess(
  user: UserState,
  router: Router,
  options: RoleGuardOptions,
) {
  if (!user?.roles) {
    return false;
  }

  if (user.roles.includes(options.allowedRole)) {
    return true;
  }

  const redirectRole = Object.entries(options.redirectByRole).find(([role]) =>
    user.roles.includes(role as UserModel.Role),
  )?.[1] as UserModel.Role | undefined;

  if (redirectRole) {
    return router.createUrlTree([UserModel.defaultRoutesForRole[redirectRole]]);
  }

  if (options.fallbackRedirectRole) {
    return router.createUrlTree([
      UserModel.defaultRoutesForRole[options.fallbackRedirectRole],
    ]);
  }

  return false;
}
